import React, { useMemo, useState } from 'react';
import type { EpicSession } from '../../../../../../shared/types/epics';
import {
  buildRetryClusters,
  type RetryCluster,
  type AttemptTone,
} from '../../../../../../shared/lib/retryClusters';
import { agentEmoji } from '../../../../lib/agentEmoji';
import { SessionRow } from '../../../../components/epicDetail/AgentRunRow';
import { StatusBadge, type StatusTone } from '../../../../components/ui';
import { formatCost } from '../format';

// ---------------------------------------------------------------------------
// Retry clusters (FORGE-224) — Option A
//
// A force-complete is normal process flow: the runtime kills a wandering agent
// and relaunches it. The flat table interleaves those retries and reads as
// chaos. Instead we fold runs into one row per agent role, show an amber→green
// attempt strip, and classify the role's terminal outcome (passed / passed on
// #N / abandoned / failing). Click a row to expand the individual runs.
// ---------------------------------------------------------------------------

const ATTEMPT_TONE_CLASS: Record<AttemptTone, string> = {
  ok: 'bg-emerald-500',
  force: 'bg-amber-400',
  fail: 'bg-red-500',
  running: 'bg-blue-400 animate-pulse',
  unknown: 'bg-slate-300',
};

const ATTEMPT_LABEL: Record<AttemptTone, string> = {
  ok: 'clean pass',
  force: 'force-complete (relaunched)',
  fail: 'crashed / errored',
  running: 'in flight',
  unknown: 'finished',
};

function clusterBadge(c: RetryCluster): { tone: StatusTone; label: string } {
  switch (c.status) {
    case 'passed':
      return { tone: 'success', label: 'passed' };
    case 'passed_on':
      return { tone: 'success', label: `passed on #${c.passedOnAttempt}` };
    case 'running':
      return { tone: 'inprogress', label: 'running' };
    case 'abandoned':
      return { tone: 'neutral', label: `abandoned · ${c.attemptCount} runs` };
    case 'failing':
      return { tone: 'danger', label: `failing · ${c.attemptCount} runs` };
  }
}

export const ClusterLegend: React.FC = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-2xs text-slate-500">
    <LegendDot className="bg-emerald-500" label="task complete" />
    <LegendDot className="bg-amber-400" label="force complete (relaunched — normal)" />
    <LegendDot className="bg-red-500" label="crashed" />
    <LegendDot className="bg-slate-300" label="abandoned approach" />
  </div>
);

const LegendDot: React.FC<{ className: string; label: string }> = ({ className, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`inline-block h-2.5 w-2.5 rounded-sm ${className}`} />
    {label}
  </span>
);

export const RetryClusters: React.FC<{
  sessions: EpicSession[];
  onOpenTranscript: (agentType: string, taskArn?: string | null) => void;
  hasLiveAgent?: boolean;
  /** Story already has spend recorded, so runs happened even if not listed. */
  hasCost?: boolean;
}> = ({ sessions, onOpenTranscript, hasLiveAgent, hasCost }) => {
  const clusters = useMemo(() => buildRetryClusters(sessions), [sessions]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  if (clusters.length === 0) {
    // FORGE-3xx: this list is completed-run history only (sessions), which is
    // legitimately empty for a story whose FIRST run hasn't finished yet — but
    // saying "No agent runs for this story yet" right below a live agent card
    // that's actively working reads as "nothing is happening," which is wrong.
    // Distinguish the two cases instead of a single static message.
    return (
      <div className="text-xs text-slate-500 px-4 py-4 italic">
        {hasLiveAgent
          ? 'No completed runs yet — an agent is in progress above.'
          : hasCost
            ? 'Run history isn’t listed for this story, but it has recorded spend — agents have run.'
            : 'No agent runs for this story yet.'}
      </div>
    );
  }

  const toggle = (agent: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(agent)) next.delete(agent);
      else next.add(agent);
      return next;
    });

  return (
    <div data-testid="retry-clusters">
      {clusters.map((c) => {
        const badge = clusterBadge(c);
        const isOpen = expanded.has(c.agentType);
        const dim = c.status === 'abandoned';
        return (
          <div key={c.agentType} className="border-t border-slate-100 first:border-t-0">
            <button
              type="button"
              onClick={() => toggle(c.agentType)}
              aria-expanded={isOpen}
              data-testid={`cluster-row-${c.agentType}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
                dim ? 'opacity-60' : ''
              }`}
            >
              <span className="w-3 shrink-0 text-xs text-slate-400">{isOpen ? '▾' : '▸'}</span>
              <span className="w-28 shrink-0 truncate font-mono text-xs text-slate-900">
                <span aria-hidden="true">{agentEmoji(c.agentType)}</span> {c.agentType}
              </span>
              <span className="flex min-w-[56px] flex-1 items-center gap-[3px]" aria-hidden="true">
                {c.attempts.map((a, i) => (
                  <span
                    key={a.sessionId + i}
                    title={`attempt ${i + 1}: ${ATTEMPT_LABEL[a.tone]}`}
                    className={`h-2.5 rounded-sm ${ATTEMPT_TONE_CLASS[a.tone]}`}
                    style={{
                      flexGrow: Math.max(1, Math.round((a.durationMs ?? 0) / 30_000)),
                      flexBasis: 0,
                      minWidth: '6px',
                    }}
                  />
                ))}
              </span>
              <StatusBadge tone={badge.tone} dot={c.status === 'running'}>
                {badge.label}
              </StatusBadge>
              <span className="w-16 shrink-0 text-right font-mono text-xs tabular-nums text-slate-600">
                {formatCost(c.totalCostUsd)}
              </span>
              <span className="hidden w-14 shrink-0 text-right text-xs tabular-nums text-slate-500 sm:inline">
                {c.totalIterations > 0
                  ? `${c.totalProductiveIterations}/${c.totalIterations} it`
                  : '—'}
              </span>
            </button>
            {isOpen && (
              <div
                className="bg-slate-50/60 py-1.5 pl-4 pr-2"
                data-testid={`cluster-detail-${c.agentType}`}
              >
                {/* Indented + left rail so the runs read as children of the
                    agent row above them, not as sibling top-level rows. */}
                <div className="overflow-x-auto border-l-2 border-slate-200 pl-2">
                  <table className="w-full text-xs">
                    <tbody>
                      {c.runs.map((s) => (
                        <SessionRow
                          key={s.sessionId}
                          session={s}
                          onOpenTranscript={onOpenTranscript}
                          showAgent={false}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
